import React, { useState } from 'react';
import type { EarningsBooking, EarningsResponse } from '../../api/client';

function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: timezone,
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function isoDay(iso: string, timezone: string): string {
  // YYYY-MM-DD in the venue's timezone — the "business day" of the row.
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: timezone });
}

function csvEscape(value: string | number | null | undefined): string {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadEarningsCsv(bookings: EarningsBooking[], month: string, timezone: string) {
  const header = [
    'booking_date', 'payment_date', 'court', 'customer', 'email', 'confirmation',
    'status', 'gross', 'sales_tax', 'commission', 'net_to_venue', 'promo_code', 'notes',
  ];
  const rows = bookings.map(b => [
    isoDay(b.start_time, timezone),
    isoDay(b.payment_date, timezone),
    b.court_name,
    `${b.customer_first_name} ${b.customer_last_name}`,
    b.customer_email,
    b.confirmation_code,
    b.payment_status,
    (b.amount_cents / 100).toFixed(2),
    b.pricing_detail?.salesTaxAmountCents != null ? (b.pricing_detail.salesTaxAmountCents / 100).toFixed(2) : '',
    b.platform_fee_amount != null ? (b.platform_fee_amount / 100).toFixed(2) : '',
    b.venue_amount != null ? (b.venue_amount / 100).toFixed(2) : '',
    b.promo_detail?.code || '',
    b.settlement_notes || '',
  ]);
  const csv = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `earnings-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type EarningsPanelProps = {
  earnings: EarningsResponse | null;
  earningsLoading: boolean;
  selectedMonth: string;
  months: string[];
  timezone: string;
  onMonthChange: (month: string) => void;
};

export default function EarningsPanel({
  earnings,
  earningsLoading,
  selectedMonth,
  months,
  timezone,
  onMonthChange,
}: EarningsPanelProps) {
  const s = earnings?.summary;
  const refundedCents = s?.refunded_cents ?? 0;
  const salesTaxCents = s?.sales_tax_cents ?? 0;
  const bookings = earnings?.bookings || [];

  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid var(--gray-200)',
          flexWrap: 'wrap', gap: '12px',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
            Earnings
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {bookings.length > 0 && (
            <button
              onClick={() => downloadEarningsCsv(bookings, selectedMonth, timezone)}
              style={{
                padding: '7px 14px', border: '1px solid var(--gray-300)',
                borderRadius: '8px', fontSize: '13px', background: 'white',
                color: 'var(--gray-700)', cursor: 'pointer', fontWeight: 500,
              }}
            >
              Export CSV
            </button>
          )}
          <select
            value={selectedMonth}
            onChange={e => onMonthChange(e.target.value)}
            style={{
              padding: '7px 14px', border: '1px solid var(--gray-300)',
              borderRadius: '8px', fontSize: '13px', background: 'white',
              color: 'var(--gray-700)', cursor: 'pointer', fontWeight: 500,
            }}
          >
            {months.map(m => (
              <option key={m} value={m}>
                {new Date(m + '-15').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </option>
            ))}
          </select>
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          {earningsLoading ? (
            <div className="loading" style={{ minHeight: '60px' }}><div className="spinner" /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              {[
                { label: 'Bookings', value: s?.booking_count ?? 0, isCount: true, icon: '#' },
                { label: 'Gross Revenue', value: s?.gross_cents ?? 0, icon: '$' },
                { label: 'Commission', value: s?.platform_fee_cents ?? 0, dim: true, icon: '%' },
                { label: 'Your Earnings', value: s?.venue_amount_cents ?? 0, highlight: true, icon: '=' },
                ...(salesTaxCents > 0
                  ? [{ label: 'Sales Tax Collected', value: salesTaxCents, dim: true, icon: 'T' }]
                  : []),
                ...(refundedCents > 0
                  ? [{ label: 'Refunded', value: refundedCents, refund: true, icon: 'R' }]
                  : []),
              ].map(({ label, value, isCount, dim, highlight, refund, icon }) => (
                <div key={label} style={{
                  padding: '20px', borderRadius: '12px',
                  background: highlight
                    ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
                    : refund
                      ? '#fef2f2'
                      : 'var(--gray-50)',
                  border: `1px solid ${highlight ? '#bbf7d0' : refund ? '#fecaca' : 'var(--gray-200)'}`,
                  position: 'relative',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '12px', fontWeight: 600,
                    color: refund ? '#991b1b' : dim ? 'var(--gray-400)' : 'var(--gray-500)',
                    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
                  }}>
                    <span style={{
                      width: '20px', height: '20px', borderRadius: '6px', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700,
                      background: highlight ? '#bbf7d0' : refund ? '#fecaca' : dim ? 'var(--gray-200)' : 'var(--gray-200)',
                      color: highlight ? '#15803d' : refund ? '#991b1b' : dim ? 'var(--gray-400)' : 'var(--gray-500)',
                    }}>
                      {icon}
                    </span>
                    {label}
                  </div>
                  <div style={{
                    fontSize: '24px', fontWeight: 700, letterSpacing: 0,
                    color: refund ? '#991b1b' : dim ? 'var(--gray-400)' : highlight ? '#15803d' : 'var(--gray-900)',
                  }}>
                    {isCount ? value : centsToDollars(value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!earningsLoading && bookings.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            padding: '20px 24px', borderBottom: '1px solid var(--gray-200)',
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              Booking Breakdown
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)' }}>
                  {['Date', 'Court', 'Customer', 'Gross', 'Commission', 'Your Earnings'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left', fontWeight: 600,
                      color: 'var(--gray-500)', whiteSpace: 'nowrap', fontSize: '12px',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      borderBottom: '1px solid var(--gray-200)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b: EarningsBooking, i: number) => (
                  <BookingRow
                    key={b.id}
                    booking={b}
                    index={i}
                    count={bookings.length}
                    timezone={timezone}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!earningsLoading && bookings.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>$</div>
          <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--gray-500)', marginBottom: '4px' }}>
            No payments this month
          </div>
          <div style={{ fontSize: '13px', color: 'var(--gray-400)' }}>
            Completed bookings will appear here.
          </div>
        </div>
      )}
    </>
  );
}

function DetailLine({ label, cents, bold, negative }: {
  label: string;
  cents: number;
  bold?: boolean;
  negative?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', padding: '3px 0' }}>
      <span style={{ color: bold ? 'var(--gray-900)' : 'var(--gray-500)', fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: bold ? 700 : 500, color: negative ? '#991b1b' : bold ? 'var(--gray-900)' : 'var(--gray-700)' }}>
        {negative ? '−' : ''}{centsToDollars(Math.abs(cents))}
      </span>
    </div>
  );
}

function BookingRow({
  booking,
  index,
  count,
  timezone,
}: {
  booking: EarningsBooking;
  index: number;
  count: number;
  timezone: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const isRefunded = booking.payment_status === 'refunded';
  const baseBackground = isRefunded ? '#f8fafc' : '';
  const mutedColor = isRefunded ? 'var(--gray-500)' : undefined;
  const pricing = booking.pricing_detail || null;
  const promo = booking.promo_detail || null;

  return (
    <>
    <tr
      style={{
        borderBottom: expanded ? 'none' : index < count - 1 ? '1px solid var(--gray-100)' : 'none',
        transition: 'background 0.15s',
        background: baseBackground,
        cursor: 'pointer',
      }}
      onClick={() => setExpanded(v => !v)}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--gray-50)')}
      onMouseLeave={e => (e.currentTarget.style.background = baseBackground)}
    >
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: 'var(--gray-500)', fontSize: '13px' }}>
        <span style={{
          display: 'inline-block', marginRight: '8px', fontSize: '10px', color: 'var(--gray-400)',
          transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
        }}>▶</span>
        {formatDate(booking.start_time, timezone)}
      </td>
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontWeight: 500, color: mutedColor }}>
        {booking.court_name}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, color: mutedColor }}>
            {booking.customer_first_name} {booking.customer_last_name}
          </span>
          {isRefunded && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: 999,
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              fontSize: 11,
              fontWeight: 700,
            }}>
              Refunded
            </span>
          )}
          {booking.promo_credit && (
            <span
              title={booking.settlement_notes || undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: 999,
                background: '#e0f2fe',
                color: '#075985',
                border: '1px solid #bae6fd',
                fontSize: 11,
                fontWeight: 700,
                cursor: booking.settlement_notes ? 'help' : undefined,
              }}
            >
              Promo — funded by PickleCourts
            </span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '1px' }}>{booking.customer_email}</div>
      </td>
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: isRefunded ? '#991b1b' : undefined }}>
        {centsToDollars(booking.amount_cents)}
      </td>
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: 'var(--gray-400)', fontVariantNumeric: 'tabular-nums' }}>
        {booking.platform_fee_amount != null ? centsToDollars(booking.platform_fee_amount) : 'N/A'}
      </td>
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontWeight: 600, color: isRefunded ? 'var(--gray-500)' : '#15803d', fontVariantNumeric: 'tabular-nums' }}>
        {booking.venue_amount != null ? centsToDollars(booking.venue_amount) : 'N/A'}
      </td>
    </tr>
    {expanded && (
      <tr style={{ borderBottom: index < count - 1 ? '1px solid var(--gray-100)' : 'none', background: 'var(--gray-50)' }}>
        <td colSpan={6} style={{ padding: '4px 16px 16px 40px' }}>
          <div style={{ maxWidth: '420px', fontSize: '13px' }}>
            {pricing ? (
              <>
                <DetailLine label="Court time" cents={pricing.baseAmountCents ?? 0} />
                {(pricing.rentalAmountCents ?? 0) > 0 && (
                  <DetailLine label="Equipment rental" cents={pricing.rentalAmountCents ?? 0} />
                )}
                {(pricing.serviceFeeAmountCents ?? 0) > 0 && (
                  <DetailLine label="Service fee" cents={pricing.serviceFeeAmountCents ?? 0} />
                )}
                {(pricing.salesTaxAmountCents ?? 0) > 0 && (
                  <DetailLine
                    label={`Sales tax${pricing.salesTaxBps ? ` (${(pricing.salesTaxBps / 100).toFixed(2).replace(/\.?0+$/, '')}%)` : ''}`}
                    cents={pricing.salesTaxAmountCents ?? 0}
                  />
                )}
                {promo && (promo.discountCents ?? 0) > 0 && (
                  <DetailLine
                    label={`Promo${promo.code ? ` ${promo.code}` : ''}${promo.percentOff ? ` (${promo.percentOff}% off${promo.fundedBy === 'platform' ? ', funded by PickleCourts' : ''})` : ''}`}
                    cents={promo.discountCents ?? 0}
                    negative
                  />
                )}
                <div style={{ borderTop: '1px solid var(--gray-200)', margin: '6px 0' }} />
                <DetailLine label="Customer paid" cents={booking.amount_cents} bold />
                {booking.platform_fee_amount != null && (
                  <DetailLine label="PickleCourts commission" cents={booking.platform_fee_amount} negative={booking.platform_fee_amount > 0} />
                )}
                {booking.venue_amount != null && (
                  <DetailLine label="Your earnings" cents={booking.venue_amount} bold />
                )}
                <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--gray-400)' }}>
                  Paid on {formatDate(booking.payment_date, timezone)}
                  {booking.confirmation_code ? ` · Confirmation ${booking.confirmation_code}` : ''}
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--gray-400)' }}>
                No itemized pricing was recorded for this booking.
              </div>
            )}
            {booking.settlement_notes && (
              <div style={{
                marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
                background: '#e0f2fe', border: '1px solid #bae6fd',
                fontSize: '12px', color: '#075985', lineHeight: 1.5,
              }}>
                {booking.settlement_notes}
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}
